import React from 'react';
import InsightSurface from '../shared/InsightSurface';
import { hasContent } from '../shared/DrillOver';
import { fmtCount, fmtDate, fmtSharePct, pagePath } from './lib/format';

/**
 * Key Findings — the notable panels named as one line each, inside the same
 * `InsightSurface` house style as the verdict above it (spec Tier 1 / §4.11).
 *
 * Clicking a finding opens that panel's own set of rows in the shared DrillOver
 * side panel. The backend gives a notable panel no single drill object, so the
 * frame is built from the panel's `facts.items` — values already in the payload,
 * pre-formatted, never computed — and each row keeps its real `drill` so the reader
 * can descend into that record set. A finding opens a drawer only when its panel
 * carries rows that show something; otherwise the action falls back to moving to the
 * panel in place, so a finding never opens an empty drawer.
 *
 * Source is unchanged — the panels the backend marked `emphasis: "notable"`, no
 * re-ranking or threshold of our own (§3). Colour is semantic (§C.2): `nearly_there`
 * amber (leaking), `new_queries`/`movers` emerald (growth), health panels gray.
 */

const FINDING_STYLE = {
  nearly_there: { icon: 'opportunity', tone: 'amber' },
  new_queries: { icon: 'trend', tone: 'emerald' },
  movers: { icon: 'trend', tone: 'emerald' },
  discovery_breadth: { icon: 'discovery', tone: 'gray' },
  position_bands: { icon: 'search', tone: 'gray' },
  brand_split: { icon: 'discovery', tone: 'gray' },
  sitemap_health: { icon: 'alert', tone: 'gray' },
  indexing_health: { icon: 'alert', tone: 'gray' },
};

const styleFor = (key) => FINDING_STYLE[key] || { icon: 'search', tone: 'gray' };

/**
 * Move to a panel the same way a `?panel=` deep link does — the fallback when a
 * notable panel has no rows to open in the side panel.
 */
const jumpToPanel = (panelKey) => {
  const target = typeof document !== 'undefined' && document.getElementById(`panel-${panelKey}`);
  if (!target) return;
  if (window.history?.replaceState) {
    window.history.replaceState(null, '', `#panel-${panelKey}`);
  }
  if (typeof target.scrollIntoView === 'function') {
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
  if (typeof target.focus === 'function') {
    target.focus({ preventScroll: true });
  }
};

/** `1 error` / `4 errors`, or '' when the count is not a number. */
const countLabel = (value, singular, plural) => {
  const text = fmtCount(value);
  return text ? `${text} ${Number(value) === 1 ? singular : plural}` : '';
};

/**
 * The "Last read by Google" line for a sitemap row, mirroring the Sitemaps table's
 * column: the read date, suffixed when the read is stale; a stale sitemap with no date
 * has never been read; no date and not stale shows nothing.
 */
const sitemapLastRead = (r) => {
  const date = fmtDate(r.last_downloaded);
  const stale = r.is_stale === true;
  if (date) return `Last read by Google ${date}${stale ? ' · Not read recently' : ''}`;
  return stale ? 'Not read yet' : undefined;
};

/** One drawer row per sitemap: path, last read, and its errors and warnings. */
const sitemapRow = (r) => ({
  primary: pagePath(r.path),
  secondary: sitemapLastRead(r),
  metric: [countLabel(r.errors, 'error', 'errors'), countLabel(r.warnings, 'warning', 'warnings')]
    .filter(Boolean)
    .join(' · ') || undefined,
  drill: r.drill,
});

/**
 * Build a DrillOver frame from a notable panel's own rows, mapped into the shared
 * `{ primary, secondary, metric, drill }` shape from values already in the payload.
 * Each row keeps its real drill so the reader can descend. Rows that would draw as a
 * bare divider are dropped. Null when there is nothing to open: no rows, none that show
 * anything, or a panel with no records behind it (`demand_season` is months, not
 * records, so its finding scrolls to the panel).
 */
const frameForFinding = (p) => {
  const items = Array.isArray(p.facts?.items) ? p.facts.items : [];
  if (items.length === 0 || p.panel === 'demand_season') return null;
  let rows;
  let subtitle;
  switch (p.panel) {
    case 'nearly_there':
      rows = items.map((r) => ({
        primary: r.query,
        secondary: r.page ? pagePath(r.page) : undefined,
        metric: `${fmtCount(r.missed_impressions)} missed`,
        drill: r.drill,
      }));
      break;
    case 'new_queries':
      rows = items.map((r) => ({
        primary: r.query,
        secondary: r.first_seen ? `First seen ${r.first_seen}` : undefined,
        metric: `${fmtCount(r.impressions)} shown`,
        drill: r.drill,
      }));
      break;
    case 'position_bands':
      rows = items.map((r) => ({
        primary: `Positions ${r.band}`,
        secondary: `${fmtSharePct(r.share)} of searches`,
        metric: `${fmtCount(r.queries)} searches`,
        drill: r.drill,
      }));
      break;
    case 'sitemap_health': {
      rows = items.map(sitemapRow);
      const { pagination } = p.facts;
      if (pagination?.total_pages > 1) {
        subtitle = `Showing the first ${items.length} of ${pagination.total_items} sitemaps`;
      }
      break;
    }
    default:
      rows = items.map((r) => ({ primary: r.query || r.band || r.title, drill: r.drill }));
  }
  rows = rows.filter(hasContent);
  if (rows.length === 0) return null;
  return { kind: p.panel, label: p.title, rows, ...(subtitle && { subtitle }) };
};

const WorthKnowing = ({ panels, onOpen }) => {
  const notable = Array.isArray(panels) ? panels : [];

  if (notable.length === 0) {
    return (
      <InsightSurface tone="gray" eyebrow="Key Findings" eyebrowClassName="text-gray-900 dark:text-gray-100">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Nothing stands out this period. Everything below is running as usual.
        </p>
      </InsightSurface>
    );
  }

  const findings = notable.map((p) => {
    const style = styleFor(p.panel);
    return {
      icon: style.icon,
      tone: style.tone,
      // The sentence is the only per-panel prose the payload carries; rendered
      // verbatim (§2.4.8). Falls back to the title when a notable panel arrives
      // without a sentence.
      text: p.sentence || p.title,
      onClick: () => {
        const frame = frameForFinding(p);
        if (frame && typeof onOpen === 'function') onOpen(frame);
        else jumpToPanel(p.panel);
      },
    };
  });

  return <InsightSurface tone="gray" eyebrow="Key Findings" eyebrowClassName="text-gray-900 dark:text-gray-100" findings={findings} />;
};

export default WorthKnowing;
